import React, { useState } from 'react';
import { X, Webhook, AlertCircle, CheckCircle, Copy } from 'lucide-react';

interface CreateWebhookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateWebhook: (webhook: WebhookData) => Promise<void>;
}

interface WebhookData {
  name: string;
  url: string;
  events: string[];
  retryCount?: number;
  timeoutMs?: number;
}

interface WebhookEvent {
  id: string;
  label: string;
  description: string;
}

const availableEvents: WebhookEvent[] = [
  {
    id: 'meeting.started',
    label: 'meeting.started',
    description: 'This is fired when the first participant joins a meeting.'
  },
  {
    id: 'meeting.ended',
    label: 'meeting.ended', 
    description: 'This is fired 1 minute after all the participants leave the call.'
  },
  {
    id: 'meeting.participantJoined',
    label: 'meeting.participantJoined',
    description: 'This is fired whenever a participant joins a meeting.'
  },
  {
    id: 'meeting.participantLeft',
    label: 'meeting.participantLeft',
    description: 'This is fired whenever a participant leaves a meeting. Also fired when one gets disconnected.'
  },
  {
    id: 'recording.statusUpdate',
    label: 'recording.statusUpdate',
    description: 'This is fired whenever there is a status update for a meeting recording.'
  },
  {
    id: 'livestreaming.statusUpdate',
    label: 'livestreaming.statusUpdate',
    description: 'This is fired whenever there is a status update for a live stream.'
  },
  {
    id: 'meeting.chatSynced',
    label: 'meeting.chatSynced',
    description: 'This is fired when the chat messages of a meeting are available. Fired after the meeting has ended.'
  },
  {
    id: 'meeting.transcript',
    label: 'meeting.transcript',
    description: 'This is fired when the meeting ends, and consists of the meeting transcript (if any).'
  },
  {
    id: 'meeting.summary',
    label: 'meeting.summary',
    description: 'This is fired after the meeting summary is generated, after the meeting has ended.'
  }
];

export const CreateWebhookModal: React.FC<CreateWebhookModalProps> = ({
  isOpen,
  onClose,
  onCreateWebhook
}) => {
  const [formData, setFormData] = useState<WebhookData>({
    name: '',
    url: '',
    events: [],
    retryCount: 3,
    timeoutMs: 30000
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdWebhook, setCreatedWebhook] = useState<any>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      events: [],
      retryCount: 3,
      timeoutMs: 30000
    });
    setError(null);
    setCreatedWebhook(null);
    setCopiedSecret(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleEventToggle = (eventId: string) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }));
  };

  const validateUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Webhook name is required');
      return;
    }
    
    if (!formData.url.trim()) {
      setError('Webhook URL is required');
      return;
    }
    
    if (!validateUrl(formData.url)) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    
    if (formData.events.length === 0) {
      setError('Please select at least one event to subscribe to');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await onCreateWebhook(formData);
      setCreatedWebhook(response);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create webhook');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch (err) {
      console.error('Failed to copy secret:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-light-bg-primary dark:bg-dark-bg-primary rounded-xl border border-light-border dark:border-dark-border max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center space-x-3">
            <Webhook className="w-6 h-6 text-primary-500" />
            <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
              {createdWebhook ? 'Webhook Created Successfully!' : 'Create a New Webhook'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {createdWebhook ? (
            /* Success State */
            <div className="p-6">
              <div className="text-center mb-6">
                <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
                <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Webhook "{createdWebhook.name}" created successfully!
                </h3>
                <p className="text-light-text-secondary dark:text-dark-text-secondary">
                  Your webhook is now active and will receive events immediately.
                </p>
              </div>

              {/* Webhook Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Webhook URL
                  </label>
                  <code className="block w-full p-3 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-sm font-mono text-light-text-primary dark:text-dark-text-primary">
                    {createdWebhook.url}
                  </code>
                </div>

                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Webhook Secret
                  </label>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 p-3 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-sm font-mono text-light-text-primary dark:text-dark-text-primary">
                      {createdWebhook.secret}
                    </code>
                    <button
                      onClick={() => copySecret(createdWebhook.secret)}
                      className="p-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                      title="Copy secret"
                    >
                      {copiedSecret ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-2">
                    ⚠️ Save this secret securely. It won't be shown again and is used to verify webhook authenticity.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Subscribed Events
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {createdWebhook.events.map((event: string, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 text-sm rounded-md"
                      >
                        {event}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Error Alert */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                  </div>
                </div>
              )}

              {/* View Response Format Link */}
              <div className="text-right">
                <button
                  type="button"
                  className="text-sm text-primary-500 hover:text-primary-600 underline"
                  onClick={() => window.open('https://docs.realtimekit.dev/api/webhooks', '_blank')}
                >
                  View Response Format
                </button>
              </div>

              {/* Webhook Name */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Webhook Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="First Webhook"
                  required
                />
              </div>

              {/* Webhook URL */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Webhook Target URL *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  className="w-full px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="https://nice.webhook.response/url"
                  required
                />
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                  Starting with https:// or http://
                </p>
              </div>

              {/* Events */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-3">
                  Events subscribed to:
                </label>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {availableEvents.map((event) => (
                    <div key={event.id} className="border border-light-border dark:border-dark-border rounded-lg p-4">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.events.includes(event.id)}
                          onChange={() => handleEventToggle(event.id)}
                          className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-light-text-primary dark:text-dark-text-primary">
                            {event.label}
                          </div>
                          <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
                            {event.description}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Retry Count
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={formData.retryCount}
                    onChange={(e) => setFormData(prev => ({ ...prev, retryCount: parseInt(e.target.value) || 3 }))}
                    className="w-full px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Timeout (ms)
                  </label>
                  <input
                    type="number"
                    min="1000"
                    max="60000"
                    step="1000"
                    value={formData.timeoutMs}
                    onChange={(e) => setFormData(prev => ({ ...prev, timeoutMs: parseInt(e.target.value) || 30000 }))}
                    className="w-full px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Creating...' : 'Create Webhook'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};